import { useEffect, useMemo } from 'react';
import Input from '../../../../components/Input/Input';
import { Controller, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import { booleanScheme, stringScheme } from 'src/utils/validation-schemes';
import { useNotification } from 'src/hooks/useNotification';
import {
  baseApi,
  useCancelSubscriptionMutation,
  useChangeSubscriptionMutation,
  useCreateSubscriptionMutation,
  useGetAllPlansQuery,
  useGetPaymentMethodsQuery,
} from 'src/services';
import { LoaderWrapper } from 'src/components/LoaderWrapper/LoaderWrapper';
import { Skeleton } from 'src/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { useGetAccountsQuery } from 'src/services/accountsService';
import { DialogClose, DialogFooter } from 'src/components/ui/dialog';
import { DeletePopup } from 'src/components/DeletePopup/DeletePopup';
import { BILLING_PLANS_TYPE, BILLING_SUBSCRIPTION_UPDATE_MOD } from 'src/constants/billing';
import { SubscriptionsType } from 'src/constants/subscriptions';
import { AddPaymentMethodPopup } from 'src/pages/SubscriptionPage/PaymentMethod/AddPaymentMethodPopup/AddPaymentMethodPopup';
import { ToolTip } from 'src/components/ToolTip/ToolTip';
import { useAppDispatch } from 'src/store';
import { phEvent } from 'src/utils/posthog.utils';
import { Button } from 'src/components/ui/button';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';
import { useIsCosupportManager } from 'src/hooks/useIsCosupportManager';

const WarningIcon = (
  <svg
    className="min-w-3.5"
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.41663 9.91669H7.58329V6.41669H6.41663V9.91669ZM6.99996 5.25002C7.16524 5.25002 7.30388 5.19402 7.41588 5.08202C7.52788 4.97002 7.58368 4.83158 7.58329 4.66669C7.5829 4.5018 7.5269 4.36335 7.41529 4.25135C7.30368 4.13935 7.16524 4.08335 6.99996 4.08335C6.83468 4.08335 6.69624 4.13935 6.58463 4.25135C6.47302 4.36335 6.41702 4.5018 6.41663 4.66669C6.41624 4.83158 6.47224 4.97021 6.58463 5.0826C6.69702 5.19499 6.83546 5.2508 6.99996 5.25002ZM6.99996 12.8334C6.19302 12.8334 5.43468 12.6801 4.72496 12.3737C4.01524 12.0672 3.39788 11.6517 2.87288 11.1271C2.34788 10.6025 1.93235 9.98513 1.62629 9.27502C1.32024 8.56491 1.16702 7.80657 1.16663 7.00002C1.16624 6.19346 1.31946 5.43513 1.62629 4.72502C1.93313 4.01491 2.34865 3.39755 2.87288 2.87294C3.3971 2.34833 4.01446 1.9328 4.72496 1.62635C5.43546 1.31991 6.19379 1.16669 6.99996 1.16669C7.80613 1.16669 8.56446 1.31991 9.27496 1.62635C9.98546 1.9328 10.6028 2.34833 11.127 2.87294C11.6513 3.39755 12.067 4.01491 12.3742 4.72502C12.6814 5.43513 12.8345 6.19346 12.8333 7.00002C12.8321 7.80657 12.6789 8.56491 12.3736 9.27502C12.0683 9.98513 11.6528 10.6025 11.127 11.1271C10.6013 11.6517 9.9839 12.0674 9.27496 12.3743C8.56601 12.6811 7.80768 12.8341 6.99996 12.8334ZM6.99996 11.6667C8.30274 11.6667 9.40621 11.2146 10.3104 10.3104C11.2145 9.40627 11.6666 8.3028 11.6666 7.00002C11.6666 5.69724 11.2145 4.59377 10.3104 3.6896C9.40621 2.78544 8.30274 2.33335 6.99996 2.33335C5.69718 2.33335 4.59371 2.78544 3.68954 3.6896C2.78538 4.59377 2.33329 5.69724 2.33329 7.00002C2.33329 8.3028 2.78538 9.40627 3.68954 10.3104C4.59371 11.2146 5.69718 11.6667 6.99996 11.6667Z"
      fill="black"
      stroke="black"
      strokeWidth="0.5"
    />
  </svg>
);
interface CurrentSubscriptionPlanPopupFormProps {
  // Whether the account already has a plan (change it) or not (create one).
  isPlanExist: boolean;
  closePopup: () => void;
}

// Amounts are '' until a plan is picked, then the plan's (or custom settings') numbers.
interface PlanFormValues {
  plan: { id: number | string; plan_type: string };
  monthlyFee: number | string;
  aiReplies: number | string;
  extraReplyCost: number | string;
  cancelImmediately: boolean;
}

export const CurrentSubscriptionPlanPopupForm = ({
  isPlanExist,
  closePopup,
}: CurrentSubscriptionPlanPopupFormProps) => {
  const notification = useNotification();
  const dispatch = useAppDispatch();

  const { data: plansAll, isFetching: plansAllIsFetching } = useGetAllPlansQuery(undefined);

  const { data: paymentMethods } = useGetPaymentMethodsQuery(
    {},
    { refetchOnMountOrArgChange: false },
  );

  const { data: account } = useGetAccountsQuery();

  const [
    createSubscription,
    { isLoading: createSubscriptionIsLoading, isSuccess: createSubscriptionIsSuccess },
  ] = useCreateSubscriptionMutation();

  const [
    changeSubscription,
    { isLoading: changeSubscriptionIsLoading, isSuccess: changeSubscriptionIsSuccess },
  ] = useChangeSubscriptionMutation();

  const [
    cancelSubscription,
    { isLoading: cancelSubscriptionIsLoading, isSuccess: cancelSubscriptionIsSuccess },
  ] = useCancelSubscriptionMutation();

  useLoaderWhile(
    createSubscriptionIsLoading,
    cancelSubscriptionIsLoading,
    changeSubscriptionIsLoading,
  );

  useEffect(() => {
    if (createSubscriptionIsSuccess) {
      notification.success('Subscription plan was successfully created!');
      phEvent('subscription_plan', { status: 'created' });
      closePopup();
    }
  }, [createSubscriptionIsSuccess]);

  useEffect(() => {
    if (cancelSubscriptionIsSuccess) {
      notification.success('Subscription plan was successfully canceled!');
      phEvent('subscription_plan', { status: 'cancelled' });
      closePopup();
    }
  }, [cancelSubscriptionIsSuccess]);

  useEffect(() => {
    if (changeSubscriptionIsSuccess) {
      notification.success('Subscription plan was successfully updated!');
      phEvent('subscription_plan', { status: 'updated' });
      closePopup();
    }
  }, [changeSubscriptionIsSuccess]);

  const {
    handleSubmit,
    control,
    trigger,
    watch,
    setValue,
    getValues,
    reset,
    formState: { isDirty },
  } = useForm<PlanFormValues>({
    defaultValues: {
      plan: { id: '', plan_type: '' },
      monthlyFee: '',
      aiReplies: '',
      extraReplyCost: '',
      cancelImmediately: false,
    },
    resolver: formResolver(
      object({
        cancelImmediately: booleanScheme({ required: false }),
        monthlyFee: stringScheme({ required: false }).label('Monthly Fee'),
        aiReplies: stringScheme({ required: false }).label('AI Replies'),
        extraReplyCost: stringScheme({ required: false }).label('Extra Reply Cost'),
        plan: object({
          plan_type: stringScheme({ required: false }),
          id: stringScheme({ required: false }),
        })
          .required()
          .label('Plan'),
      }),
    ),
  });

  const planWatch = watch('plan');
  const monthlyFeeWatch = watch('monthlyFee');
  const cancelImmediatelyWatch = watch('cancelImmediately');

  const usedPlan = useMemo(() => {
    return plansAll?.find((plan) => plan.id === account?.general_plan?.plan?.id);
  }, [plansAll, account]);

  const submit = (formValues: PlanFormValues) => {
    if (isPlanExist) {
      if (formValues.plan.plan_type === BILLING_PLANS_TYPE.CUSTOM) {
        changeSubscription({
          subscription_item: {
            plan_id: Number(formValues.plan.id),
            fixed_price: formValues.monthlyFee,
            usage_price: formValues.extraReplyCost,
            request_limit: formValues.aiReplies,
          },
        });
      } else {
        changeSubscription({
          subscription_item: {
            plan_id: Number(formValues.plan.id),
          },
        });
      }
    } else if (formValues.plan.plan_type === BILLING_PLANS_TYPE.CUSTOM) {
      createSubscription({
        subscription_item: {
          plan_id: Number(formValues.plan.id),
          fixed_price: formValues.monthlyFee,
          usage_price: formValues.extraReplyCost,
          request_limit: formValues.aiReplies,
        },
      });
    } else {
      createSubscription({
        subscription_item: {
          plan_id: Number(formValues.plan.id),
        },
      });
    }
  };

  const UpdateAccountTimeout = () => {
    return setTimeout(() => dispatch(baseApi.util.invalidateTags(['Accounts'])), 2000);
  };

  const updateSubscriptionNowHandler = () => {
    const formValues = getValues();
    if (formValues.plan.plan_type === BILLING_PLANS_TYPE.CUSTOM) {
      changeSubscription({
        subscription_update_mode: BILLING_SUBSCRIPTION_UPDATE_MOD.NOW,
        subscription_item: {
          plan_id: Number(formValues.plan.id),
          fixed_price: formValues.monthlyFee,
          usage_price: formValues.extraReplyCost,
          request_limit: formValues.aiReplies,
        },
      }).then(() => UpdateAccountTimeout());
    } else {
      changeSubscription({
        subscription_update_mode: BILLING_SUBSCRIPTION_UPDATE_MOD.NOW,
        subscription_item: {
          plan_id: Number(formValues.plan.id),
        },
      }).then(() => UpdateAccountTimeout());
    }
  };

  const cancelPlanHandler = () => {
    cancelSubscription({ immediately: cancelImmediatelyWatch }).then(() => UpdateAccountTimeout());
  };

  useEffect(() => {
    if (plansAll?.length) {
      if (usedPlan) {
        reset({
          plan: { id: usedPlan?.id, plan_type: usedPlan?.plan_type },
          monthlyFee:
            account?.general_plan?.custom_plan_settings?.fixed_price || usedPlan.price || '',
          aiReplies:
            account?.general_plan?.custom_plan_settings?.request_limit ||
            usedPlan.request_limit ||
            '',
          extraReplyCost:
            account?.general_plan?.custom_plan_settings?.usage_price ||
            usedPlan.extra_request_price ||
            '',
          cancelImmediately: false,
        });
      }
    }
  }, [usedPlan, account, plansAll]);

  const isCosupportManager = useIsCosupportManager();

  const handleCreatePaymentMethodSuccess = (is?: boolean) => {
    if (is) {
      UpdateAccountTimeout();
    }
  };

  const sortByPricePlans = plansAll ? [...plansAll]?.sort((a, b) => a.price - b.price) : [];

  return (
    <>
      <form id="add-subscription-plan-form" onSubmit={handleSubmit(submit)}>
        <div className={'input-container'}>
          <LoaderWrapper
            isLoading={plansAllIsFetching}
            loader={
              <div className="flex flex-col">
                <span className="pt-1 text-xs leading-5 font-medium text-ebony_opacity_50 select-none">
                  Plan
                </span>
                <Skeleton className="w-full h-[52px]" />
              </div>
            }
          >
            <Controller
              control={control}
              name="plan"
              render={({ field: { onChange } }) => (
                <div className="flex flex-col">
                  <label htmlFor="subscription-plan">Plan</label>
                  {sortByPricePlans?.length ? (
                    <Select
                      defaultValue={JSON.stringify(usedPlan)}
                      onValueChange={(value) => {
                        const parsedValue = JSON.parse(value);
                        onChange({
                          id: parsedValue?.id,
                          plan_type: parsedValue?.plan_type,
                        });
                        setValue('monthlyFee', parsedValue?.price);
                        setValue('aiReplies', parsedValue?.request_limit);
                        setValue('extraReplyCost', parsedValue?.extra_request_price);
                      }}
                    >
                      <SelectTrigger id="subscription-plan" className="w-full">
                        <SelectValue
                          placeholder={
                            <div className="flex flex-col">
                              <div className="flex gap-2 items-center">
                                <p className="font-medium text-base text-start relative">
                                  {sortByPricePlans[0]?.name}
                                </p>
                                {isCosupportManager && (
                                  <span className="rounded-full capitalize bg-ebony bg-opacity-10 px-2 text-xs opacity-60">
                                    {sortByPricePlans[0]?.plan_type}
                                  </span>
                                )}
                              </div>
                              <span className="text-sm font-semibold opacity-40 text-wrap">
                                ${sortByPricePlans[0]?.price} – {sortByPricePlans[0]?.request_limit}{' '}
                                AI replies included, ${sortByPricePlans[0]?.extra_request_price}{' '}
                                extra reply.
                              </span>
                            </div>
                          }
                          defaultValue={JSON.stringify(usedPlan)}
                          className="[&>p]:font-bold"
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {sortByPricePlans?.map((plan) => {
                          return (
                            <SelectItem key={plan?.id} value={JSON.stringify(plan)}>
                              <div className="flex flex-col">
                                <div className="flex gap-2 items-center">
                                  <p className="font-medium text-base text-start relative">
                                    {plan?.name}
                                  </p>
                                  {isCosupportManager && (
                                    <span className="rounded-full capitalize bg-ebony bg-opacity-10 px-2 text-xs opacity-60">
                                      {plan?.plan_type}
                                    </span>
                                  )}
                                </div>
                                <span className="text-sm font-semibold opacity-40 text-wrap">
                                  ${plan?.price} – {plan?.request_limit} AI replies included, $
                                  {plan?.extra_request_price} extra reply.
                                </span>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  ) : (
                    'No plans'
                  )}
                </div>
              )}
            />
          </LoaderWrapper>
        </div>
        <div className={'input-container'}>
          <Controller
            control={control}
            name="monthlyFee"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                prefix="$"
                readonly={planWatch?.plan_type !== BILLING_PLANS_TYPE.CUSTOM}
                type={'text'}
                label={'Monthly Fee'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                onBlur={() => trigger('monthlyFee')}
              />
            )}
          />
        </div>
        <div className={'input-container'}>
          <Controller
            control={control}
            name="aiReplies"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                readonly={planWatch.plan_type !== BILLING_PLANS_TYPE.CUSTOM}
                type={'text'}
                label={'AI replies'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                onBlur={() => trigger('aiReplies')}
              />
            )}
          />
        </div>
        <div className={'input-container'}>
          <Controller
            control={control}
            name="extraReplyCost"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                prefix="$"
                readonly={planWatch.plan_type !== BILLING_PLANS_TYPE.CUSTOM}
                type={'text'}
                label={'Extra reply cost'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                onBlur={() => trigger('extraReplyCost')}
              />
            )}
          />
        </div>
      </form>
      <DialogFooter>
        {!isPlanExist && (
          <DialogClose asChild>
            <Button variant="pill-outline-dark" size="pill" style={{ width: 'auto' }}>
              Cancel
            </Button>
          </DialogClose>
        )}
        {isPlanExist && (
          <div className="relative">
            <ToolTip
              triggerClassName="absolute -right-1"
              side="top"
              content={
                <>
                  Your subscription will stay active until <br /> the end of the current billing
                  cycle.
                </>
              }
              trigger={WarningIcon}
            />
            <DeletePopup
              deleteHandler={cancelPlanHandler}
              triggerIcon={
                <Button
                  variant="pill-outline-red"
                  size="pill"
                  style={{ width: 'auto' }}
                  className="min-w-28"
                >
                  Cancel plan
                </Button>
              }
              deleteBtnText="Yes, cancel"
              title={'Cancel subscription plan?'}
              description={
                <span className="flex flex-col gap-4">
                  <span>
                    Are you sure you want to cancel{' '}
                    <span className="font-bold">{usedPlan?.name}</span>?
                  </span>
                  {/*{isCosupportManager && (*/}
                  {/*  <span className="flex items-center gap-4">*/}
                  {/*    <span>Cancel immediately?</span>*/}
                  {/*    <Switch*/}
                  {/*      type="green"*/}
                  {/*      checked={cancelImmediatelyWatch}*/}
                  {/*      onCheckedChange={(value) => {*/}
                  {/*        setValue('cancelImmediately', value);*/}
                  {/*      }}*/}
                  {/*    />*/}
                  {/*  </span>*/}
                  {/*)}*/}
                </span>
              }
            />
          </div>
        )}
        {Boolean(Object.keys(account?.general_plan || {})?.length) &&
          account?.general_plan?.plan?.plan_type !== SubscriptionsType.trial && (
            <div className="relative">
              <ToolTip
                triggerClassName="absolute -right-1"
                side="top"
                content={
                  <>
                    Schedule the plan change for your <br /> next billing cycle.
                  </>
                }
                trigger={WarningIcon}
              />
              <Button
                variant="pill-outline-dark"
                size="pill"
                style={{ width: 'auto' }}
                disabled={!isDirty}
                className="min-w-28"
                type="submit"
                form="add-subscription-plan-form"
              >
                Update next billing cycle
              </Button>
            </div>
          )}
        {!paymentMethods?.data?.length ? (
          <AddPaymentMethodPopup
            paymentMethods={paymentMethods}
            handleCreateSuccess={handleCreatePaymentMethodSuccess}
            defaultOpen
            submitBtnText={'Save'}
            trigger={
              <Button
                variant="pill-green"
                size="pill"
                disabled={!isDirty}
                style={{ width: 'auto' }}
              >
                Add payment method
              </Button>
            }
          />
        ) : (
          <div className="relative">
            {!plansAllIsFetching &&
              planWatch?.id !== usedPlan?.id &&
              (usedPlan?.price > Number(monthlyFeeWatch) ? (
                <ToolTip
                  triggerClassName="absolute -right-1"
                  side="left"
                  content={
                    <>
                      Your current plan will remain active until <br /> the end of this billing
                      cycle. The new plan will <br /> take effect starting the next billing cycle.
                    </>
                  }
                  trigger={WarningIcon}
                />
              ) : (
                <ToolTip
                  triggerClassName="absolute -right-1"
                  side="left"
                  content={
                    <>
                      Upgrade now and pay only the prorated difference. <br /> Your billing cycle
                      will stay the same.
                    </>
                  }
                  trigger={WarningIcon}
                />
              ))}
            <Button
              variant="pill-green"
              size="pill"
              disabled={!isDirty}
              style={{ width: 'auto' }}
              type="submit"
              form={isPlanExist ? '' : 'add-subscription-plan-form'}
              onClick={isPlanExist ? updateSubscriptionNowHandler : () => {}}
            >
              {isPlanExist ? 'Update now' : 'Save'}
            </Button>
          </div>
        )}
      </DialogFooter>
    </>
  );
};
